import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  TextField,
  Grid,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HeightIcon from '@mui/icons-material/Height';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

/**
 * Компонент для управления дополнительными выходами (подача или обратка)
 * Компактный табличный UI аналогичный CenterFittingsUI
 */
const AdditionalOutletsUI = ({
  outlets,
  outletCount,
  onUpdateCount,
  onUpdateOutlet,
  onDeleteOutlet,
  fittingCategories,
  getFittingsByCategory,
  isFittingCompatibleWithMainPipe,
  mainPipeDiameter,
  circuitCount,
  bottomCircuitCount = 0,
  onIncompatibilityWarning,
  availableDiameters,
  fittingsData,
  collectorType, // 'supply' или 'return'
  mode = 'distribution', // Режим работы: 'distribution' или 'hydraulic'
  currentRow = 'supply', // Текущая линия (supply/return для distribution)
  isCompact = false, // Компактный режим (для распределительного коллектора)
}) => {
  const defaultDirection = collectorType === 'supply' ? 'up' : 'down';

  // Варианты размещения
  const placementOptions = [
    { value: 'center', label: 'По середине' },
    { value: 'betweenCircuits', label: 'Между контурами' },
    { value: 'betweenFittings', label: 'Между патрубками' },
    { value: 'distance', label: 'На расстоянии' },
  ];

  // Определяем количество вариантов для выбора
  const getOptionsCount = (positionType, referenceRow = 'supply') => {
    // Если текущая линия совпадает с referenceRow, используем circuitCount, иначе bottomCircuitCount
    const count = referenceRow === currentRow ? circuitCount : bottomCircuitCount;
    
    if (positionType === 'betweenCircuits') {
      return count - 1;
    } else if (positionType === 'betweenFittings') {
      return count * 2 - 1;
    }
    return 0;
  };

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Количество:</Typography>
            <IconButton
              size="small"
              onClick={() => onUpdateCount(-1)}
              disabled={outletCount <= 0}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 24, textAlign: 'center' }}>
              {outletCount}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onUpdateCount(1)}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      {/* Таблица дополнительных выходов */}
      {outletCount > 0 && (
        <TableContainer sx={{ maxHeight: '13.75rem', border: '0.0625rem solid #e0e0e0', borderRadius: 1, mb: 2 }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, p: 0.5, width: 30 }}>№</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Категория</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Фитинг</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Вылет</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Размещение</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5 }}>Значение</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5, width: 60 }}>Линия</TableCell>
                <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}>Тип</TableCell>
                {/* В компактном режиме скрываем колонку направления */}
                {!isCompact && (
                  <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}>↕</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, p: 0.5, width: 40 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outlets.map((outlet, index) => {
                const fittingsInCategory = getFittingsByCategory(
                  outlet.fittingCategory || 'Ниппели',
                  outlet // Передаем outlet для определения типа выхода
                );

                return (
                  <TableRow key={outlet.id}>
                    <TableCell sx={{ p: 0.5 }}>{index + 1}</TableCell>

                    {/* Категория */}
                    <TableCell sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={outlet.fittingCategory || 'Ниппели'}
                        onChange={(e) => {
                          const newCategory = e.target.value;
                          const fittingsInNewCategory = getFittingsByCategory(newCategory);
                          const firstCompatibleFitting = fittingsInNewCategory.find((f) => f.isCompatible);

                          if (!firstCompatibleFitting) {
                            onIncompatibilityWarning('Невозможно сварить изделие, в связи с основным диаметром гребенки');
                            return;
                          }

                          onUpdateOutlet(index, 'fittingCategory', newCategory);
                          onUpdateOutlet(index, 'fittingId', firstCompatibleFitting.id);
                          onUpdateOutlet(index, 'fittingLength', null);
                          onUpdateOutlet(index, 'flangePipeLength', null);
                        }}
                        sx={{ minWidth: 100, fontSize: '0.75rem' }}
                      >
                        {fittingCategories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id} sx={{ fontSize: '0.8rem' }}>
                            {cat.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    {/* Фитинг */}
                    <TableCell sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={outlet.fittingId}
                        onChange={(e) => {
                          const newFittingId = e.target.value;
                          if (!isFittingCompatibleWithMainPipe(newFittingId, mainPipeDiameter, outlet)) {
                            onIncompatibilityWarning('Невозможно сварить изделие, в связи с основным диаметром гребенки');
                            return;
                          }
                          onUpdateOutlet(index, 'fittingId', newFittingId);
                          onUpdateOutlet(index, 'fittingLength', null);
                          onUpdateOutlet(index, 'flangePipeLength', null);
                        }}
                        sx={{ minWidth: 100, fontSize: '0.75rem' }}
                      >
                        {fittingsInCategory.map((f) => (
                          <MenuItem
                            key={f.id}
                            value={f.id}
                            onClick={(e) => {
                              if (!f.isCompatible) {
                                e.preventDefault();
                                e.stopPropagation();
                                onIncompatibilityWarning('Невозможно сварить изделие, в связи с основным диаметром гребенки');
                              }
                            }}
                            sx={{
                              fontSize: '0.8rem',
                              ...(!f.isCompatible && {
                                color: 'text.disabled',
                                textDecoration: 'line-through',
                              }),
                            }}
                          >
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    {/* Вылет */}
                    <TableCell sx={{ p: 0.5 }}>
                      {outlet.fittingId?.startsWith('Flanec_') ||
                        outlet.fittingId?.startsWith('Nak_Gaika_') ||
                        outlet.fittingId?.startsWith('Shaib_Nar_Rezba_') ? (
                        <TextField
                          type="number"
                          size="small"
                          value={outlet.flangePipeLength === 0 ? '' : (outlet.flangePipeLength ?? '')}
                          onChange={(e) => onUpdateOutlet(index, 'flangePipeLength', e.target.value === '' ? null : e.target.value)}
                          placeholder="Авто"
                          sx={{ width: 85 }}
                          InputProps={{
                            endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                            sx: { fontSize: '0.8rem' },
                          }}
                        />
                      ) : (
                        <TextField
                          type="number"
                          size="small"
                          value={outlet.fittingLength === 0 ? '' : (outlet.fittingLength ?? '')}
                          onChange={(e) => onUpdateOutlet(index, 'fittingLength', e.target.value === '' ? null : e.target.value)}
                          placeholder="Авто"
                          sx={{ width: 85 }}
                          InputProps={{
                            endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                            sx: { fontSize: '0.8rem' },
                          }}
                        />
                      )}
                    </TableCell>

                    {/* Размещение */}
                    <TableCell sx={{ p: 0.5 }}>
                      <Select
                        size="small"
                        value={outlet.positionType || 'center'}
                        onChange={(e) => onUpdateOutlet(index, 'positionType', e.target.value)}
                        sx={{ minWidth: 110, fontSize: '0.75rem' }}
                      >
                        {placementOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    {/* Значение (динамическое поле) */}
                    <TableCell sx={{ p: 0.5 }}>
                      {(outlet.positionType === 'betweenCircuits' || outlet.positionType === 'betweenFittings') ? (
                        <Select
                          size="small"
                          value={outlet.betweenIndex || 0}
                          onChange={(e) => onUpdateOutlet(index, 'betweenIndex', e.target.value)}
                          sx={{ minWidth: 80, fontSize: '0.75rem' }}
                        >
                          {Array.from({ length: Math.max(0, getOptionsCount(outlet.positionType, outlet.referenceRow)) }, (_, i) => (
                            <MenuItem key={i} value={i}>
                              {i + 1} и {i + 2}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : outlet.positionType === 'center' ? (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          —
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={outlet.distanceFromEdge ?? 60}
                            onChange={(e) => onUpdateOutlet(index, 'distanceFromEdge', e.target.value)}
                            sx={{ width: 85 }}
                            InputProps={{
                              endAdornment: <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>мм</Typography>,
                              sx: { fontSize: '0.8rem' },
                            }}
                          />
                          <Tooltip title={outlet.distanceDirection === 'right' ? 'Отсчёт справа' : 'Отсчёт слева'}>
                            <IconButton
                              size="small"
                              onClick={() => onUpdateOutlet(index, 'distanceDirection', outlet.distanceDirection === 'right' ? 'left' : 'right')}
                              sx={{
                                p: 0.3,
                                color: outlet.distanceDirection === 'right' ? 'primary.main' : 'text.secondary',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                            >
                              <SwapHorizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>

                    {/* Линия (подача/обратка) */}
                    <TableCell sx={{ p: 0.5, textAlign: 'center' }}>
                      {(outlet.positionType === 'betweenCircuits' || outlet.positionType === 'betweenFittings') ? (
                        <Tooltip title={outlet.referenceRow === 'return' ? 'Обратная линия' : 'Подающая линия'}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const newValue = outlet.referenceRow === 'return' ? 'supply' : 'return';
                              onUpdateOutlet(index, 'referenceRow', newValue);
                              onUpdateOutlet(index, 'betweenIndex', 0);
                            }}
                            sx={{ 
                              p: 0.5,
                              color: outlet.referenceRow === 'return' ? '#2196f3' : '#f44336',
                              '&:hover': { bgcolor: 'action.hover' }
                            }}
                          >
                            {outlet.referenceRow === 'return' ? (
                              <ArrowBackIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <ArrowForwardIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>

                    {/* Тип выхода (короткий/сквозной) */}
                    <TableCell sx={{ p: 0.5 }}>
                      {(() => {
                        const currentDirection = outlet.direction || defaultDirection;
                        const currentOutletType = outlet.outletType || 'short';
                        
                        // Проверяем, можно ли использовать сквозной тип для текущего направления
                        // Подача: сквозной только для "вверх"
                        // Обратка: сквозной только для "вниз"
                        const isValidForThrough = (collectorType === 'supply' && currentDirection === 'up') || 
                                                   (collectorType === 'return' && currentDirection === 'down');
                        
                        // Если выбран сквозной, но направление неправильное - показываем предупреждение
                        const isDisabled = !isValidForThrough && currentOutletType === 'through';
                        
                        // Формируем текст подсказки
                        let tooltipTitle = currentOutletType === 'short' ? 'Короткий выход' : 'Сквозной выход';
                        if (!isValidForThrough && currentOutletType === 'short') {
                          tooltipTitle += ' (сквозной недоступен для этого направления)';
                        }
                        
                        return (
                          <Tooltip title={tooltipTitle}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={isDisabled}
                                onClick={() => {
                                  const newType = currentOutletType === 'short' ? 'through' : 'short';
                                  // Переключаем только если направление позволяет
                                  if (newType === 'through' && !isValidForThrough) {
                                    return; // Не переключаем на сквозной при неправильном направлении
                                  }
                                  
                                  // Проверяем совместимость текущего фитинга с новым типом выхода
                                  const currentFittingId = outlet.fittingId;
                                  if (currentFittingId) {
                                    const isCompatible = isFittingCompatibleWithMainPipe(
                                      currentFittingId, 
                                      mainPipeDiameter, 
                                      { ...outlet, outletType: newType }
                                    );
                                    
                                    if (!isCompatible) {
                                      // Текущий фитинг несовместим, пробуем найти максимально доступный совместимый
                                      const category = outlet.fittingCategory || 'Ниппели';
                                      const availableFittings = getFittingsByCategory(category, { ...outlet, outletType: newType });
                                      const compatibleFittings = availableFittings.filter(f => f.isCompatible);
                                      
                                      if (compatibleFittings.length > 0) {
                                        // Находим фитинг с максимальным диаметром
                                        const maxDiameterFitting = compatibleFittings.reduce((max, current) => {
                                          const currentData = fittingsData[current.id];
                                          const maxData = fittingsData[max.id];
                                          return (currentData?.diameter || 0) > (maxData?.diameter || 0) ? current : max;
                                        });
                                        
                                        // Переключаем на максимально доступный фитинг
                                        onUpdateOutlet(index, 'fittingId', maxDiameterFitting.id);
                                        onUpdateOutlet(index, 'fittingLength', null);
                                        onUpdateOutlet(index, 'flangePipeLength', null);
                                      } else {
                                        // Не нашли совместимый фитинг - показываем предупреждение
                                        onIncompatibilityWarning(`Нет доступных фитингов категории "${category}" для ${newType === 'short' ? 'короткого' : 'сквозного'} выхода`);
                                        return;
                                      }
                                    }
                                  }
                                  
                                  onUpdateOutlet(index, 'outletType', newType);
                                }}
                                sx={{
                                  p: 0.3,
                                  color: currentOutletType === 'through' 
                                    ? (isDisabled ? 'error.main' : 'secondary.main') 
                                    : (isValidForThrough ? 'text.secondary' : 'action.disabled'),
                                  '&:hover': { bgcolor: isDisabled ? 'transparent' : 'action.hover' },
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                }}
                              >
                                {currentOutletType === 'short' ? (
                                  <HeightIcon fontSize="small" />
                                ) : (
                                  <UnfoldMoreIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>

                    {/* Направление (стрелки) - скрыто в компактном режиме */}
                    {!isCompact && (
                      <TableCell sx={{ p: 0.5 }}>
                        <Tooltip title={(outlet.direction || defaultDirection) === 'up' ? 'Вверх' : 'Вниз'}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const currentDirection = outlet.direction || defaultDirection;
                              const newDirection = currentDirection === 'up' ? 'down' : 'up';
                              onUpdateOutlet(index, 'direction', newDirection);
                              
                              // Если выход сквозной и новое направление несовместимо - сбрасываем на короткий
                              const currentOutletType = outlet.outletType || 'short';
                              if (currentOutletType === 'through') {
                                const isValidForThrough = (collectorType === 'supply' && newDirection === 'up') || 
                                                         (collectorType === 'return' && newDirection === 'down');
                                if (!isValidForThrough) {
                                  onUpdateOutlet(index, 'outletType', 'short');
                                }
                              }
                            }}
                            sx={{
                              p: 0.3,
                              color: 'primary.main',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            {(outlet.direction || defaultDirection) === 'up' ? (
                              <ArrowUpwardIcon fontSize="small" />
                            ) : (
                              <ArrowDownwardIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}

                    {/* Удалить */}
                    <TableCell sx={{ p: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteOutlet(index)}
                        sx={{ color: 'error.main' }}
                        title="Удалить выход"
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default AdditionalOutletsUI;
